import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule, FormControl, FormsModule, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ReservationService } from 'src/app/services/reservation.service';
import { ReservationModel } from 'src/app/models/reservation.model';
import { RoomModel } from 'src/app/models/room.model';
import { GuestModel } from 'src/app/models/guest.model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { DocumentTypeEnum } from 'src/app/shared/enumerators/document-type.enum';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CountryModel } from 'src/app/models/country.model';
import { Subject } from 'rxjs';
import { SwalFireService } from 'src/app/shared/services/swal-fire.service';
import { CountryService } from 'src/app/shared/services/country.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDivider } from '@angular/material/divider';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { GuestService } from 'src/app/services/guest.service';
import { ReservationDetailService } from 'src/app/services/reservation-detail.service';
import { ReservationStatusEnum } from 'src/app/shared/enumerators/reservation-status.enum';
import { ToastService } from 'src/app/shared/services/toast.service';

@Component({
  selector: 'app-check-in',
  styleUrl: './check-in.component.css',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    MatOptionModule,
    MatCheckboxModule,
    NgxMatSelectSearchModule,
    MatExpansionModule,
    MatDivider,
    DragDropModule,
    MatIcon,
    MatIconModule,
    MatButtonModule
  ],
  templateUrl: './check-in.component.html'
})
export class CheckInComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private reservationSrv = inject(ReservationService);
  private toastr = inject(ToastService);
  private countryService = inject(CountryService);
  private guestSrv = inject(GuestService);
  private reservationDetailSrv = inject(ReservationDetailService);
  private router = inject(Router);

  reservationId!: number;
  reservationData: ReservationModel;
  countries: CountryModel[] = [];
  filteredCountries: CountryModel[] = [];
  countryFilterCtrl = new FormControl();
  destroy$ = new Subject<void>();

  guestsForm: FormGroup = this.fb.group({
    guests: this.fb.array([])
  });;

  documentTypes = Object.keys(DocumentTypeEnum)
    .filter(key => !isNaN(Number(DocumentTypeEnum[key as any])))
    .map(key => ({
      value: DocumentTypeEnum[key as keyof typeof DocumentTypeEnum],
      label: key
    }));

  ngOnInit() {
    this.countryService.getCountries().subscribe(data => {
      this.countries = data;
      this.filteredCountries = this.countries;

      this.countryFilterCtrl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(search => {
          const term = search?.toLowerCase() || '';
          this.filteredCountries = this.countries.filter(country =>
            country.name.toLowerCase().includes(term)
          );
        });
    });

    this.getReservation();
  }



  getReservation() {
    this.route.queryParams.subscribe((params) => {
      this.reservationId = +params['id'];
      if (this.reservationId) {
        this.reservationSrv.getById(this.reservationId).subscribe({
          next: (data) => {
            if (!this.isFullyPaid(data)) {
              this.toastr.warning('Reserva', 'La reserva no se encuentra totalmente abonada');
              this.router.navigate([`payment`], { queryParams: { id: this.reservationId } });
              return;
            }

            let guestIndex = 0;
            this.reservationData = data;
            for (var i = 0; i < data.reservationDetails.length; i++) {

              let rd = data.reservationDetails[i];

              let guests = rd.guests as GuestModel[];

              for (var g = 0; g < guests.length; g++) {
                let guest = guests[g];
                var bp = guest.documentNumber == data.bookingPerson.documentNumber;

                this.addGuestForRoom(rd.id)

                this.guestsForm.get(['guests', guestIndex])?.enable();
                this.guestsForm.get(['guests', guestIndex])?.patchValue(guest);

                if (bp) {
                  (this.guestsForm.get(['guests', guestIndex]) as FormGroup).controls.isBookingPerson.patchValue(true);
                }
                guestIndex++;

              }
            }
          },
          error: () => this.toastr.error('Reserva', 'Error al obtener la reserva.')
        });
      }
    });
  }

  getGuest(index) {
    return (this.guestsForm.get(['guests', index]) as FormGroup).value;
  }

  isFullyPaid(reservation: ReservationModel): boolean {
    const totalPaid = reservation.payments?.reduce((sum, p) => sum + p.amount, 0) || 0;
    return totalPaid >= reservation.totalPrice;
  }
  get connectedDropLists(): string[] {
    return this.reservationData.reservationDetails.map(d => `room-${d.id}`);
  }

  get guests(): FormArray {
    return this.guestsForm.get('guests') as FormArray;
  }

  addGuestForRoom(reservationDetailId: number): void {
    this.guests.push(this.createGuestGroup(reservationDetailId));
  }

  canAddGuest(reservationDetailId: number) {
    var count = this.getGuestsForRoom(reservationDetailId).length
    return count < this.reservationData.guestCount && this.guests.length < this.reservationData.guestCount;
  }

  showSaveBtn() {
    return this.reservationData?.status === ReservationStatusEnum.Pending ||
      this.reservationData?.status === ReservationStatusEnum.Reserved
  }

  desableSaveBtn() {
    return this.guestsForm.invalid || this.guestsForm.value.guests.length < this.reservationData?.guestCount
  }

  createGuestGroup(reservationDetailId: number): FormGroup {
    let group = this.fb.group({
      reservationDetailId: [reservationDetailId],
      areaCode: ['', [Validators.required, Validators.pattern(/^\+?[1-9]\d{1,13}$/)]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^(?!15)\d{6,10}$/)]],
      email: ['', [Validators.required, Validators.email]],
      countryOfOrigin: ['', Validators.required],
      originAddress: ['', Validators.required],
      documentType: [null, Validators.required],
      documentNumber: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      destinationCountry: ['', Validators.required],
      nationality: ['', Validators.required],
      occupation: [''],
      vehicleMakeModel: [''],
      isBookingPerson: [false],
      licensePlate: [''],
      observations: [''],
      id: [0]
    });

    const documentTypeCtrl = group.get('documentType');
    // const documentNumberCtrl = group.get('documentNumber');

    documentTypeCtrl!.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.tryAutoFillGuest(group);
    });

    group.disable();
    group.controls.documentType.enable();
    group.controls.documentNumber.enable();

    // documentNumberCtrl!.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
    //   this.tryAutoFillGuest(group);
    // });

    return group;
  }

  tryAutoFillGuest(group: FormGroup) {

    const docType = group.get('documentType')!.value;
    const docNumber = group.get('documentNumber')!.value;

    const isBP = group.get('isBookingPerson')!.value;

    var guestVal = (this.guests.value as Array<GuestModel>).filter(x => x.documentNumber == docNumber && x.documentType == docType);

    if (guestVal.length > 1) {
      this.toastr.error("Huésped", "El huésped ya se encuentra registrado");
      return;
    }

    if (isBP) {
      group.enable();
      return;
    }


    if (docType && docNumber) {

      var documentType = this.documentTypes.find(x => x.value == docType as DocumentTypeEnum).label
      this.guestSrv.getByDocNumber(docNumber, documentType).subscribe({
        next: (data: any) => {
          group.enable();
          if (data.items.length > 0) {
            group.patchValue(data.items[0]);
          }
          else {
            group.controls.id.patchValue(0)
          }
        },
        error: () => {
        }
      });
    }
  }

  getGuestsForRoom(reservationDetailId: number): FormGroup[] {
    return this.guests.controls.filter(
      (ctrl: AbstractControl) =>
        ctrl.get('reservationDetailId')?.value === reservationDetailId
    ) as FormGroup[];
  }

  get connectedDropListIds(): string[] {
    return this.reservationData.reservationDetails.map(d => 'room-' + d.id);
  }

  removeGuest(guestGroup: FormGroup): void {
    const index = this.guests.controls.indexOf(guestGroup);
    if (index !== -1) this.guests.removeAt(index);
  }

  onGuestDropped(event: CdkDragDrop<FormGroup[]>, newReservationDetailId: number) {
    const guest: FormGroup = event.item.data;

    // Actualiza el ID de habitación
    guest.get('reservationDetailId')?.setValue(newReservationDetailId);

    // Reordenamiento manual si se movió dentro de la misma habitación
    if (event.previousContainer === event.container) {
      const guestList = this.getGuestsForRoom(newReservationDetailId);
      moveItemInArray(guestList, event.previousIndex, event.currentIndex);
    } else {
      // Movimiento entre habitaciones → nada más que actualizar ID
    }
  }

  submit(confirm: boolean): void {
    if (this.guestsForm.valid) {
      // const guests: GuestModel[] = this.guestsForm.value.guests;

      this.reservationData.reservationDetails.forEach(d => {
        let guests = (this.guestsForm.value.guests as Array<any>).filter(x => x.reservationDetailId == d.id) as GuestModel[]
        d.guests = guests;
      });

      this.reservationDetailSrv.saveReservationDetails(this.reservationData.reservationDetails, confirm).subscribe({
        next: (guest: any) => {
          this.reservationData.status = ReservationStatusEnum.CheckedIn;
          this.toastr.success(
            'Check-in',
            'Se realizó el check-in exitozamente.'
          );
        },
        error: () => {
          this.toastr.success(
            'Check-in',
            'Ocurrió un problema, intentelo nuevamente.'
          );
        }
      });
    }
  }
}

